import { useField } from 'formik';
import styled, { css } from 'styled-components';

import { usePermissionAwareDisabled } from '../../../contexts/TeamPermissionContext';
import { Div, StyledUtilsProps } from '../../helpers/StyledUtils';
import Text from '../../base/Text';

interface Props extends StyledUtilsProps {
  options: { value: string; label: string; description?: string; disabled?: boolean }[];
  name: string;
  // Stack the cards one under the other instead of side by side.
  vertical?: boolean;
}

export const StyledRadioCard = styled(Div)<{
  disabled?: boolean;
  checked: boolean;
  vertical?: boolean;
}>(
  ({ theme, disabled, checked, vertical }) => css`
    margin: 0;
    flex-grow: 1;
    /* Stacked, the column already sets the width, and 100% would leave out the padding. */
    width: ${vertical ? 'auto' : '100%'};
    border-radius: ${theme.radius.normal};
    box-shadow: ${theme.shadows.neutral.base};

    ${checked &&
    css`
      box-shadow: ${theme.shadows.neutral.highlight};
    `}

    cursor: pointer;

    cursor: pointer;
    &:hover {
      cursor: pointer;
    }
    ${disabled &&
    css`
      cursor: not-allowed;
      &:hover {
        cursor: not-allowed;
      }
    `}

    input {
      appearance: none;
      background-color: #fff;

      min-width: ${theme.pxToRem(18)};
      min-height: ${theme.pxToRem(18)};
      border: 1px solid ${theme.colors.outline.neutral};
      border-radius: 50%;
      display: grid;
      place-content: center;
      background-color: ${theme.colors.surface.base.surface};

      &:hover:not(:disabled) {
        border-color: ${theme.colors.outline.hover.neutral};
        cursor: pointer;
      }

      &:checked:not(:disabled) {
        border-color: ${theme.colors.outline.primary};
        background-color: ${theme.colors.surface.base.primary};
      }

      &:disabled {
        border: 1px solid ${theme.colors.outline.neutral};
        background-color: ${theme.colors.surface.base.disabled};
        color: ${theme.colors.on.neutral.secondary_neutral};
        cursor: not-allowed;
      }

      &:disabled + span {
        color: ${theme.colors.on.neutral.secondary_neutral};
      }

      &:checked {
        &::before {
          content: '';
          height: 8px;
          width: 8px;
          background-color: ${disabled
            ? theme.colors.on.neutral.disabled
            : theme.colors.on.hue.primary};
          border-radius: 50%;
        }
      }
    }
  `,
);

const RadioCardsInput: React.FC<Props> = ({ options, name, vertical, ...props }) => {
  const field_disabled = usePermissionAwareDisabled();
  const [field] = useField(name);

  return (
    <div role="group">
      <Div
        {...props}
        flex={{ align: 'stretch', gap: vertical ? 2 : 4, direction: vertical ? 'column' : 'row' }}>
        {options.map(({ value, label, description, disabled }) => (
          <StyledRadioCard
            key={value}
            as="label"
            disabled={disabled}
            vertical={vertical}
            p={description ? 4 : { y: 1.5, x: 3 }}
            checked={field.value === value}>
            <Div flex={{ align: 'center', gap: 3 }}>
              <input
                {...field}
                checked={field.value === value}
                value={value}
                name={name}
                disabled={field_disabled || disabled}
                type="radio"
              />
              <Text semi={!!description} muted={disabled}>
                {label}
              </Text>
            </Div>
            {description && (
              <Text muted m={{ t: 1 }} size="xs">
                {description}
              </Text>
            )}
          </StyledRadioCard>
        ))}
      </Div>
    </div>
  );
};

export default RadioCardsInput;
